import React, { useState, useEffect } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import ContentHeaderSection from "../../shared/common/ContentHeaderSection";
import apiCall from "shared/services/ApiAdapter";

const EvaluationAssignmentsContent = ({
  navigateTo,
  selectedBatchId,
  selectedCourseTitle,
  selectedEvaluationId,
  useMockData,
  displayToast,
}) => {
  const [assignments, setAssignments] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();

  useEffect(() => {
    const fetchEvaluationAssignments = async () => {
      const fallbackBatchId = (() => {
        try {
          return sessionStorage.getItem("app_selected_batch_id");
        } catch (e) {
          return null;
        }
      })();

      const effectiveBatchId = selectedBatchId || fallbackBatchId;

      if (!effectiveBatchId || !selectedEvaluationId) {
        console.log(
          `[EvaluationAssignmentsContent] Missing data: selectedBatchId=${effectiveBatchId}, selectedEvaluationId=${selectedEvaluationId}`
        );
        return;
      }

      console.log(
        `[EvaluationAssignmentsContent] Fetching assignments for batch ${effectiveBatchId} and evaluation ${selectedEvaluationId}`
      );

      try {
        // Get all assignments for this batch
        const batchAssignments = await apiCall(
          `/batch-assignments/batch/${effectiveBatchId}/assignments`,
          useMockData,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        );

        console.log(
          `[EvaluationAssignmentsContent] Batch assignments:`,
          batchAssignments
        );

        // Normalize pageable or array response
        const assignmentsList = Array.isArray(batchAssignments)
          ? batchAssignments
          : batchAssignments?.content ?? [];

        // Filter assignments for the specific evaluation
        const normalizeId = (val) => (val == null ? null : String(val));
        const extractIdFromLink = (link) => {
          if (!link) return null;
          const last = String(link).split("/").filter(Boolean).pop();
          // If last segment contains non-digits (like '4'), keep digits; else use as-is
          if (!last) return null;
          const digits = last.replace(/\D/g, "");
          return digits.length ? digits : last;
        };

        const targetId = normalizeId(selectedEvaluationId);
        let evaluationAssignments = (assignmentsList || []).filter(
          (assignment) => {
            const fromObj = normalizeId(assignment?.evaluation?.id);
            const fromLink = normalizeId(
              extractIdFromLink(assignment?.evaluationLink)
            );
            return fromObj === targetId || fromLink === targetId;
          }
        );

        console.log(
          `[EvaluationAssignmentsContent] Filtered assignments:`,
          evaluationAssignments
        );
        // Fallback: If empty, try direct filtered query by batch and evaluation
        if (!evaluationAssignments || evaluationAssignments.length === 0) {
          const direct = await apiCall(
            `/assignments?batchId.equals=${effectiveBatchId}&evaluationId.equals=${selectedEvaluationId}&size=1000`,
            useMockData,
            "GET",
            null,
            startLoading,
            stopLoading,
            displayToast
          );
          const directList = Array.isArray(direct)
            ? direct
            : direct?.content ?? [];
          evaluationAssignments = directList;
          console.log(
            `[EvaluationAssignmentsContent] Fallback direct assignments:`,
            directList
          );
        }

        setAssignments(evaluationAssignments || []);

        // Get evaluation details
        const evaluationDetails = await apiCall(
          `/evaluations/${selectedEvaluationId}`,
          useMockData,
          "GET",
          { id: selectedEvaluationId },
          startLoading,
          stopLoading,
          displayToast
        );

        console.log(
          `[EvaluationAssignmentsContent] Evaluation details:`,
          evaluationDetails
        );
        setEvaluation(evaluationDetails);
      } catch (error) {
        console.error("Failed to fetch evaluation assignments:", error);
        displayToast("Failed to fetch assignments", "error");
      }
    };

    fetchEvaluationAssignments();
  }, [
    selectedBatchId,
    selectedEvaluationId,
    useMockData,
    startLoading,
    stopLoading,
    displayToast,
  ]);

  const getStatusColor = (assignment) => {
    if (assignment.submittedDateTime) {
      return "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100";
    } else if (
      assignment.expiryDateTime &&
      new Date(assignment.expiryDateTime) < new Date()
    ) {
      return "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100";
    } else {
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100";
    }
  };

  // const getStatusText = (assignment) => {
  //   if (assignment.submittedDateTime) {
  //     return t("submittedAssignments");
  //   } else if (
  //     assignment.expiryDateTime &&
  //     new Date(assignment.expiryDateTime) < new Date()
  //   ) {
  //     return t("expiredAssignments");
  //   } else {
  //     return t("pendingAssignments");
  //   }
  // };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "N/A";
    return new Date(dateTime).toLocaleString();
  };

  const rightSideContentTop = (
    <>
      <div className="relative w-full max-w-40">
        <input
          type="text"
          placeholder={t("search")}
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 text-gray-900"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
          🔍
        </span>
      </div>
      <div className="flex items-center justify-center w-10 h-10 text-xl text-gray-100 bg-gray-800 rounded-full cursor-pointer dark:bg-gray-600 dark:text-gray-200">
        ⚙️
      </div>
    </>
  );

  const handleApproveAssignment = async (assignment) => {
    try {
      const payload = {
        ...assignment,
        status: "Completed",
        updatedDate: new Date().toISOString(),
      };
      await apiCall(
        `/assignments/${assignment.id}`,
        false,
        "PATCH",
        payload,
        startLoading,
        stopLoading,
        () => { }
      );
      displayToast("Status assignment successful");

      // Create a new array with the updated assignment
      const updatedAssignments = assignments.map(assgnment =>
        assgnment.id === assignment.id ? { ...assgnment, status: "Completed" } : assgnment
      );
      // Update the state with the new array
      setAssignments(updatedAssignments);
    } catch (e) {
      displayToast("Failed to submit assignment");
    }
  }


  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                navigateTo(
                  "batchEvaluations",
                  selectedCourseTitle,
                  evaluation?.course?.id,
                  selectedBatchId,

                )
              }
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
              aria-label="Go back to batch evaluations"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>
            <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
              {evaluation ? evaluation.name : "Evaluation"}
            </span>
          </div>
        }
        subtitle={t("viewEvaluationAssignments")}
        rightContentTop={rightSideContentTop}
        rightContentBottom={null}
      />

      {/* Evaluation Details Card */}
      {evaluation && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Evaluation Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Chapter
              </p>
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                {evaluation.chapter}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Topic</p>
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                {evaluation.conceptTopic || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Difficulty
              </p>
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                {evaluation.difficulty}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Questions
              </p>
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                {evaluation.numberOfQuestions}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Time Limit
              </p>
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                {evaluation.maxTimeMinutes} minutes
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Due Date
              </p>
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                {evaluation.expiryDateTime?.split("T")[0]}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
              <svg
                className="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t("totalAssignments")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {assignments.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-green-100 dark:bg-green-900">
              <svg
                className="w-6 h-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t("submittedAssignments")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {assignments.filter((a) => a.submittedDateTime).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-yellow-100 dark:bg-yellow-900">
              <svg
                className="w-6 h-6 text-yellow-600 dark:text-yellow-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t("pendingAssignments")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {
                  assignments.filter(
                    (a) =>
                      !a.submittedDateTime &&
                      (!a.expiryDateTime ||
                        new Date(a.expiryDateTime) >= new Date())
                  ).length
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-red-100 dark:bg-red-900">
              <svg
                className="w-6 h-6 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t("expiredAssignments")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {
                  assignments.filter(
                    (a) =>
                      a.expiryDateTime &&
                      new Date(a.expiryDateTime) < new Date() &&
                      !a.submittedDateTime
                  ).length
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Student Assignments
        </h3>

        {assignments.length === 0 ? (
          <div className="text-center text-gray-600 dark:text-gray-400 py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              ></path>
            </svg>
            <p className="mt-2">{t("noEvaluationAssignmentsFound")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {assignments.map((assignment) => {
              return (
                <div
                  key={assignment.id}
                  className={`rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-l-4
                     ${assignment.status === "Completed"
                      ? "border-green-500"
                      : assignment.status === "InReview"
                        ? "border-yellow-500"
                        : "border-gray-400"
                    } bg-white dark:bg-gray-800 shadow-sm`}
                >
                  <div className="flex items-center justify-between gap-4">
                    {/* Left: Student Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <img
                          src={
                            assignment.student?.profilePictureUrl ||
                            `https://placehold.co/40x40/e0f2f7/00796b?text=${assignment.student?.name?.charAt(0) || "S"}`
                          }
                          alt={assignment.student?.name || "Student"}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div className="flex flex-col">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {assignment.student?.name || "Unknown Student"}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {assignment.student?.academicClass} | {assignment.student?.curriculum}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Center: Score */}
                    {assignment.score !== null && (
                      <div className="text-center">
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t("assignmentScore")}</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {Math.round((assignment.score / assignment.evaluation.numberOfQuestions) * 100)}%
                        </p>
                      </div>
                    )}

                    {/* Right: Dates and Status */}
                    <div className="flex flex-col items-end text-right space-y-1">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium 
                          ${assignment.status === "Completed"
                            ? "bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200"
                            : assignment.status === "InReview"
                              ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-200"
                              : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                          }`}
                      >
                        {assignment.status}
                      </span>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Sent: {formatDateTime(assignment.sentDateTime)}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Due: {formatDateTime(assignment.expiryDateTime)}
                      </p>

                    </div>
                  </div>
                </div>

              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluationAssignmentsContent;
